/** Lowercase hyphen slug (PyPI, RubyGems, Composer vendor segments). */
export const toHyphenSlug = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/@/g, '')
    .replace(/[._/\\]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')

/** True when namespace uses Maven-style dotted coordinates (groupId). */
const hasMatrixCoordinateSegments = (libraryNamespace?: string): boolean =>
  Boolean(libraryNamespace?.trim().includes('.'))

const parseNpmScope = (libraryName: string): { scope?: string; packageName: string } => {
  const trimmed = libraryName.trim().replace(/^@/, '')
  const slash = trimmed.indexOf('/')
  if (slash === -1) {
    return { packageName: trimmed }
  }

  return {
    scope: trimmed.slice(0, slash),
    packageName: trimmed.slice(slash + 1),
  }
}

/**
 * Composer / Packagist (→ PHP column in matrix).
 * @see GRAFTCODE Package Installation Matrix
 */
export const buildComposerPublishId = (
  repository: string,
  libraryNamespace: string | undefined,
  libraryName: string
): string => {
  switch (repository) {
    case 'maven': {
      const vendor = libraryNamespace?.trim() ? toHyphenSlug(libraryNamespace) : undefined
      return vendor ? `graft-maven-${vendor}/${libraryName}` : `graft-maven/${libraryName}`
    }
    case 'packagist': {
      const vendor = libraryNamespace?.trim() ? toHyphenSlug(libraryNamespace) : undefined
      return vendor ? `graft-packagist-${vendor}/${libraryName}` : `graft-packagist/${libraryName}`
    }
    case 'npm': {
      const { scope, packageName } = parseNpmScope(libraryName)
      return scope ? `graft-npm-${toHyphenSlug(scope)}/${packageName}` : `graft-npm/${libraryName}`
    }
    case 'nuget':
    case 'pypi':
    case 'rubygems':
      return `graft-${repository}/${libraryName}`
    default:
      return `graft-${repository}/${libraryName}`
  }
}

/**
 * npm (→ Node.js column): @graft/{source}-…
 */
export const buildNpmPublishId = (
  repository: string,
  libraryNamespace: string | undefined,
  libraryName: string
): string => {
  if (hasMatrixCoordinateSegments(libraryNamespace)) {
    return `@graft/${repository}-groupid_${libraryNamespace!.trim()}_artifactid_${libraryName}`
  }

  if (repository === 'packagist') {
    const vendor = libraryNamespace?.trim()
    return vendor
      ? `@graft/packagist-vendor_${vendor}_packagename_${libraryName}`
      : `@graft/packagist-packagename_${libraryName}`
  }

  if (repository === 'npm') {
    if (libraryName.includes('/')) {
      const scoped = libraryName.replace(/^@/, '').replace(/\//g, '__')
      return `@graft/${repository}-${scoped}`
    }
    return `@graft/npm-${libraryName}`
  }

  return `@graft/${repository}-${libraryName}`
}

/**
 * PyPI / RubyGems pip & gem installs (→ Python / Ruby columns).
 */
export const buildHyphenatedFeedPublishId = (
  repository: string,
  libraryNamespace: string | undefined,
  libraryName: string
): string => {
  if (hasMatrixCoordinateSegments(libraryNamespace)) {
    return `graft-${repository}-groupid-${toHyphenSlug(libraryNamespace!)}-artifactid-${toHyphenSlug(libraryName)}`
  }

  if (repository === 'packagist') {
    const vendor = libraryNamespace?.trim()
    const packageSlug = toHyphenSlug(libraryName)
    return vendor
      ? `graft-packagist-vendor-${toHyphenSlug(vendor)}-packagename-${packageSlug}`
      : `graft-packagist-packagename-${packageSlug}`
  }

  if (repository === 'npm') {
    const { scope, packageName } = parseNpmScope(libraryName)
    if (scope) {
      return `graft-npm-scope-${toHyphenSlug(scope)}-packageName-${toHyphenSlug(packageName)}`
    }
    return `graft-npm-${toHyphenSlug(libraryName)}`
  }

  return `graft-${repository}-${toHyphenSlug(libraryName)}`
}

/**
 * NuGet / dotnet (→ .NET Core column).
 */
export const buildNugetPublishId = (
  repository: string,
  libraryNamespace: string | undefined,
  libraryName: string,
  normalizedLibraryName: string
): string => {
  if (hasMatrixCoordinateSegments(libraryNamespace)) {
    return `graft.${repository}.groupid_${libraryNamespace!.trim()}_artifactid_${libraryName}`
  }

  if (repository === 'packagist') {
    const vendor = libraryNamespace?.trim()
    return vendor
      ? `graft.packagist.vendor_${vendor}_packagename_${libraryName}`
      : `graft.packagist.packagename_${libraryName}`
  }

  if (repository === 'npm') {
    const { scope, packageName } = parseNpmScope(libraryName)
    if (scope) {
      return `graft.npm__${scope}__${packageName}`
    }
  }

  return `graft.${repository}.${normalizedLibraryName}`
}
